import React, { useEffect, useState } from "react";
import { getCourses, getPublishedCourses } from "../../api/courseApi";
import { getLatestMaterialProgress } from "../../api/materialsApi";
import { Input } from "../../components/ui/input";
import {
  Search,
  LayoutGrid,
  List as ListIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ContinueLearningCard from "../../components/courses/ContinueLearningCard";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../../components/ui/carousel";
import CourseCard from "../../components/courses/CourseCard";
import CourseListItem from "../../components/courses/CourseListItem";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { CURRENT_COURSE_NAME_KEY } from "@/utils/localStoragekeys";
import noCourseImage from "@/assets/nocourse_image.svg";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";

export default function Courses() {
  const navigate = useNavigate();
  const [continueLearning, setContinueLearning] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(8);
  const [total, setTotal] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  // Fetch Continue Learning courses
  useEffect(() => {
    const fetchContinueLearning = async () => {
      setContinueLoading(true);
      localStorage.removeItem(CURRENT_COURSE_NAME_KEY);

      try {
        const response = await getLatestMaterialProgress(10); 
        if (response.success && response.data) {
          const transformedCourses = response.data.map((material) => {
            const progress = material.materialProgress?.[0];
            return {
              id: material.topic?.courseId || material.topicId,
              title: material.topic?.course?.name || "Unknown Course",
              materialTitle: material.title,
              image: material.fileUpload.thumbnailUrl
                ? import.meta.env.VITE_CDN_LINK +
                  material.fileUpload?.thumbnailUrl
                : noCourseImage,
              topicsCompleted: progress?.completionPercentage || 0,
              totalTopics: 100, // Using percentage as completion
              topicName: material.topic?.name,
              materialId: material.id,
              lastPosition: progress?.lastPosition || 0,
              watchTime: progress?.watchTime || 0,
            };
          });
          setContinueLearning(transformedCourses);
        } else {
          setContinueLearning([]);
        }
      } catch (error) {
        console.error("Failed to fetch continue learning materials:", error);
        setContinueLearning([]);
      } finally {
        setContinueLoading(false);
      }
    };

    fetchContinueLearning();
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchCourses = async () => {
      try {
        const filters = {
          search: search || undefined,
          page,
          limit: pageSize,
          sortBy: "createdAt",
          sortOrder: "asc",
          isActive: true,
        };
        const response = await getCourses(filters);
        if (response.success) {
          setCourses(
            response.data.map((course) => ({
              ...course,
              title: course.name,
              minPassPercentage: course.minPassPercentage ?? 60,
              duration: course.duration ?? 10,
              image: course.coverImage
                ? import.meta.env.VITE_CDN_LINK + course.coverImage
                : noCourseImage,
            })).sort((a, b) =>
              new Date(a.createdAt) - new Date(b.createdAt)
            )
          );

          setTotal(response.pagination?.total || response.data.length);
        } else {
          setError(response.message || "Failed to fetch courses");
        }
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to fetch courses"));
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [search, page, pageSize, retryCount]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4">
      <div className="mx-auto">
        {/* Continue Learning Section */}
        {continueLearning.length > 0 && (
          <section className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">
              Continue Learning
            </h2>

            {continueLoading ? (
              <div className="flex gap-4 overflow-x-auto pb-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-gray-200 rounded-2xl min-w-[280px] sm:min-w-[320px] h-48 sm:h-56 animate-pulse flex-shrink-0"
                  />
                ))}
              </div>
            ) : (
              <div className="relative">
                <Carousel className="w-full">
                  <CarouselContent className="">
                    {continueLearning.map((course, idx) => (
                      <CarouselItem
                        key={idx}
                      >
                        <ContinueLearningCard
                          course={course}
                          onContinue={() => {
                            if (course.materialId) {
                              navigate(`/dashboard/courses/${course.id}/${course.materialId}`, {
                                state: {
                                  courseName: course.title,
                                  materialTitle: course.materialTitle
                                }
                              });
                            } else {
                              navigate(`/dashboard/courses/${course.id}`, {
                                state: {
                                  courseName: course.title
                                }
                              });
                            }
                          }} 
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="hidden sm:flex -left-4" />
                  <CarouselNext className="hidden sm:flex -right-4" />
                </Carousel>
              </div>
            )}
          </section>
        )}

        {/* All Courses Section */}
        <section>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">
            All Courses
          </h2>

          {/* Search and View Controls */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="relative flex-1 max-w-md">
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-4 py-2 sm:py-3 rounded-full border-gray-300 bg-white text-sm sm:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search courses..."
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            </div>

            <div className="flex gap-2 sm:ml-auto">
              <Button
                variant={view === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("grid")}
                className={`rounded-lg px-3 py-2 sm:px-4 sm:py-2 ${view === "grid" ? "bg-[#5D93A5] text-white" : "bg-white text-gray-500"}`}
              >
                <LayoutGrid className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="ml-2 hidden sm:inline">Grid</span>
              </Button>
              <Button
                variant={view === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("list")}
                className={`rounded-lg px-3 py-2 sm:px-4 sm:py-2 ${view === "list" ? "bg-[#5D93A5] text-white" : "bg-white text-gray-500"}`}
              >
                <ListIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="ml-2 hidden sm:inline">List</span>
              </Button>
            </div>
          </div>

          {/* Courses Display */}
          {loading ? (
            <div
              className={
                view === "grid"
                  ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
                  : "flex flex-col gap-4 sm:gap-6"
              }
            >
              {[...Array(pageSize)].map((_, i) => (
                <div
                  key={i}
                  className={
                    view === "grid"
                      ? "bg-gray-200 rounded-2xl aspect-[4/5] animate-pulse"
                      : "bg-gray-200 rounded-2xl h-32 sm:h-40 animate-pulse"
                  }
                />
              ))}
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-8 sm:py-12">
              <p className="text-lg">{error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setRetryCount((count) => count + 1)}
              >
                Try again
              </Button>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-gray-500 text-center py-8 sm:py-12">
              <p className="text-lg">No courses found.</p>
            </div>
          ) : view === "grid" ? (
            <div className="flex items-start justify-around content-evenly flex-wrap gap-y-6">
              {courses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onViewDetails={() => navigate(`/dashboard/courses/${course.id}`, {
                    state: {
                      courseName: course.title
                    }
                  })}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4 sm:gap-6">
              {courses.map((course) => (
                <CourseListItem
                  key={course.id}
                  course={course}
                  onClick={() => {
                    navigate(`/dashboard/courses/${course.id}`, {
                      state: {
                        courseName: course.title
                      }
                    })
                  }}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-8">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="ml-1 hidden sm:inline">Previous</span>
              </Button>

              <div className="flex gap-1 mx-2">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={"outline"}
                      onClick={() => setPage(pageNum)}
                      className={`rounded-lg w-8 h-8 sm:w-10 sm:h-10 p-0 ${page === pageNum && "bg-[var(--primary-font)] text-white"}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="rounded-lg hover:text-white hover:bg-[var(--primary-font)] transition-all"
              >
                <span className="mr-1 hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
